import type { ComponentProps } from 'react';
import React, { useId } from 'react';

import { cn } from '../cn';
import { extractSizeFromClassName, getBgColor } from '../common';
import { usePlatformImage } from '../contexts';

export const AvatarPropsShape = ['square', 'circle', 'hex'] as const;

// Regular hexagon clip-path (pointy top/bottom)
// For a mathematically regular hexagon: width = height * sqrt(3)/2 ≈ 0.866
// Centered horizontally: side margins = (1 - 0.866) / 2 ≈ 6.7%
export const HEX_CLIP_PATH = 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)';
export type AvatarProps = Omit<ComponentProps<'div'>, 'onError'> & {
  shape?: (typeof AvatarPropsShape)[number];
  srcs: (string | null | undefined)[];
  alt: string;
  coloredBackground?: boolean;
  /**
   * Render a transparency checkerboard behind the image instead of the solid
   * slate fill. Use for background-removed (`nobg`) images so the see-through
   * areas read as transparent rather than as a grey background.
   */
  checkerboard?: boolean;
  /**
   * How the image fills the frame. `'cover'` (default) crops to fill — right for
   * face avatars. `'contain'` fits the whole image inside, letterboxing with the
   * background — use for transparent cutouts (e.g. a background-removed / trimmed
   * image) whose true shape should be visible instead of cropped away.
   */
  objectFit?: 'cover' | 'contain';
  onError?: (index: number, s: string) => void;
  placeholder?: React.JSX.Element;
  tooltip?: string;
};

export default function Avatar({
  className,
  alt,
  srcs,
  coloredBackground,
  checkerboard,
  objectFit = 'cover',
  style,
  onError,
  placeholder,
  tooltip,
  shape = 'square',
  ref,
  ...props
}: AvatarProps) {
  const Image = usePlatformImage();
  const id = useId();
  const anchorName = tooltip ? `--avatar-tip-${id.replace(/:/g, '')}` : undefined;
  const filteredSrcs = srcs.filter((src) => src !== null && src !== undefined);
  const [srcIndex, setSrcIndex] = React.useState(0);
  const initials = alt
    ?.split(' ')
    .map((a) => a[0]?.toLocaleUpperCase())
    .join('')
    .toUpperCase();

  const _onError = () => {
    const src = filteredSrcs[srcIndex];
    if (src) onError?.(srcIndex, src);
    setSrcIndex((i) => i + 1);
  };

  const src = filteredSrcs[srcIndex];
  const bgColor = src == null && coloredBackground && initials != null ? getBgColor(initials) : undefined;

  const size = extractSizeFromClassName(className);
  const isResponsive = size === 'full';
  const numericSize = typeof size === 'number' ? size : undefined;

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex aspect-square h-full shrink-0 items-center justify-center overflow-hidden rounded-md font-medium',
        'text-slate-900 dark:text-slate-50',
        checkerboard ? 'bg-checkerboard-light dark:bg-checkerboard-dark' : 'bg-slate-200 dark:bg-slate-700',
        { 'rounded-full': shape === 'circle', 'rounded-md': shape === 'square', 'rounded-none': shape === 'hex' },
        {
          'text-4xs': numericSize != null && numericSize <= 8,
          'text-3xs': numericSize != null && numericSize > 8 && numericSize <= 14,
          'text-2xs': (numericSize != null && numericSize > 14 && numericSize <= 20) || isResponsive,
          'text-xs': numericSize != null && numericSize > 20 && numericSize <= 32,
          'text-base': numericSize != null && numericSize > 32 && numericSize <= 40,
          'text-xl': numericSize != null && numericSize > 40 && numericSize <= 64,
        },
        tooltip && 'group/avatar',
        props.onClick && 'cursor-pointer',
        className,
      )}
      style={{ background: bgColor, color: bgColor ? '#fff' : undefined, clipPath: shape === 'hex' ? HEX_CLIP_PATH : undefined, ...(anchorName ? { anchorName } as React.CSSProperties : undefined), ...style }}
      {...props}>
      {src && (
        <Image
          alt={alt}
          src={src}
          width={numericSize}
          height={numericSize}
          fill={!numericSize}
          onError={_onError}
          className={cn('h-full w-full', objectFit === 'contain' ? 'object-contain' : 'object-cover')}
        />
      )}
      {!src && (
        <>
          {placeholder != null && React.cloneElement(placeholder, { className: 'size-2/3' })}
          {!placeholder && <span>{initials}</span>}
        </>
      )}
      {tooltip && (
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            positionAnchor: anchorName,
            positionArea: 'top',
            positionTryFallbacks: 'flip-block',
            margin: 0,
            inset: 'auto',
            marginBottom: '6px',
            zIndex: 50,
          } as React.CSSProperties}
          className={cn(
            'opacity-0 group-hover/avatar:opacity-100',
            'pointer-events-none transition-opacity',
            'whitespace-nowrap px-2 py-1 rounded-md bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 text-xs font-normal shadow-lg',
          )}>
          {tooltip}
        </span>
      )}
    </div>
  );
}
